<template>
    <ModalOverlay v-if="show" @click="handleOverlayClick">
        <ModalContainer @click.stop>
            <ModalHeader>
                <ModalTitle>구분 관리</ModalTitle>
                <CloseButton @click="closeModal">✕</CloseButton>
            </ModalHeader>
            <ModalContent>
                <AddButton @click="handleAddType">+ 구분 추가</AddButton>

                <TypeTable v-if="companyTypes.length > 0">
                    <TableHeader>
                        <TableHeaderRow>
                            <TableHeaderCell>유형 코드</TableHeaderCell>
                            <TableHeaderCell>유형 이름</TableHeaderCell>
                            <TableHeaderCell>삭제</TableHeaderCell>
                        </TableHeaderRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="type in companyTypes" :key="type.num">
                            <TableCell>{{ type.type_code }}</TableCell>
                            <TableCell>{{ type.type_name }}</TableCell>
                            <TableCell>
                                <DeleteButton v-if="type.companykey === companyKey || type.share === 1"
                                    @click="handleDeleteType(type.num, type.type_name)">
                                    삭제
                                </DeleteButton>
                                <span v-else style="color: #9ca3af; font-size: 12px;">삭제 불가</span>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </TypeTable>

                <EmptyMessage v-else>
                    등록된 구분이 없습니다. 구분을 추가해주세요.
                </EmptyMessage>
            </ModalContent>
        </ModalContainer>
    </ModalOverlay>
</template>

<script>
import * as CompanyTypeModalCss from '@/assets/styles/manage/modal/CompanyTypeModalCss.js';

export default {
    name: 'CompanyTypeModal',

    components: {
        ...CompanyTypeModalCss
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        companyTypes: {
            type: Array,
            default: () => []
        },
        companyKey: {
            type: Number,
            required: true
        }
    },

    emits: ['close', 'updated'],

    methods: {
        closeModal() {
            this.$emit('close');
        },

        handleOverlayClick() {
            this.closeModal();
        },

        async handleAddType() {
            try {
                const { value: formValues } = await this.$swal.fire({
                    title: '구분 추가',
                    html: `
                        <div style="text-align: left; padding: 10px;">
                            <div style="margin-bottom: 15px;">
                                <label style="display: block; margin-bottom: 5px; font-weight: 600;">유형 코드</label>
                                <input id="type_code" class="swal2-input" placeholder="예: 14, 15" style="width: 100%;">
                                <small style="color: #666;">숫자 2자리 코드를 입력하세요</small>
                            </div>
                            <div style="margin-bottom: 15px;">
                                <label style="display: block; margin-bottom: 5px; font-weight: 600;">유형 이름</label>
                                <input id="type_name" class="swal2-input" placeholder="예: 신규유형" style="width: 100%;">
                            </div>
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText: '추가',
                    cancelButtonText: '취소',
                    customClass: {
                        container: 'swal2-high-zindex',
                        popup: 'swal2-high-zindex'
                    },
                    preConfirm: () => {
                        const typeCode = document.getElementById('type_code').value.trim();
                        const typeName = document.getElementById('type_name').value.trim();
                        const share = 0;

                        if (!typeCode || !typeName) {
                            this.$swal.showValidationMessage('모든 필드를 입력해주세요.');
                            return false;
                        }

                        if (!/^\d{2}$/.test(typeCode)) {
                            this.$swal.showValidationMessage('유형 코드는 2자리 숫자여야 합니다.');
                            return false;
                        }

                        return { type_code: typeCode, type_name: typeName, share };
                    }
                });

                if (formValues) {
                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/addCompanyType`,
                        {
                            companykey: this.companyKey,
                            type_code: formValues.type_code,
                            type_name: formValues.type_name,
                            share: formValues.share
                        },
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (response.data.success) {
                        this.$swal.fire({
                            title: '추가 완료',
                            text: '구분이 추가되었습니다.',
                            icon: 'success',
                            timer: 1500,
                            showConfirmButton: false,
                            customClass: {
                                container: 'swal2-high-zindex',
                                popup: 'swal2-high-zindex'
                            }
                        });
                        // 부모 컴포넌트에 업데이트 알림
                        this.$emit('updated');
                    }
                }
            } catch (error) {
                console.error('구분 추가 실패:', error);
                this.$swal.fire({
                    title: '추가 실패',
                    text: error.response?.data?.message || '구분 추가 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인',
                    customClass: {
                        container: 'swal2-high-zindex',
                        popup: 'swal2-high-zindex'
                    }
                });
            }
        },

        async handleDeleteType(typeNum, typeName) {
            try {
                const { isConfirmed } = await this.$swal.fire({
                    title: '구분 삭제 확인',
                    text: `"${typeName}" 구분을 정말로 삭제하시겠습니까?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#dc2626',
                    customClass: {
                        container: 'swal2-high-zindex',
                        popup: 'swal2-high-zindex'
                    }
                });

                if (!isConfirmed) return;

                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/deleteCompanyType/${typeNum}`,
                    {
                        params: { companykey: this.companyKey },
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success) {
                    this.$swal.fire({
                        title: '삭제 완료',
                        text: '구분이 삭제되었습니다.',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        customClass: {
                            container: 'swal2-high-zindex',
                            popup: 'swal2-high-zindex'
                        }
                    });
                    // 부모 컴포넌트에 업데이트 알림
                    this.$emit('updated');
                }
            } catch (error) {
                console.error('구분 삭제 실패:', error);
                this.$swal.fire({
                    title: '삭제 실패',
                    text: error.response?.data?.message || '구분 삭제 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인',
                    customClass: {
                        container: 'swal2-high-zindex',
                        popup: 'swal2-high-zindex'
                    }
                });
            }
        }
    }
};
</script>